import React, { useLayoutEffect, useRef, useState } from "react";
import * as THREE from "three";
import { useNavigate } from "react-router-dom";
import Rocket from "./rocket.js";
import Saturn from "./saturn.js";
import Moon from "./moon.js";
import Sun from "./sun.js";
import BlackHole from "./blackhole.js";
import Earth from "./earth.js";
import Galaxy from "./galaxy.js";
import game_rules from "./game_rules.png";
import starsImage from "./stars.png";

const Game = () => {
  const gameContainerRef = useRef(null);
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  const handlePauseToggle = () => {
    if (isPaused) {
      setShowPopup(false);
      setIsPaused(false);
    } else {
      setIsPaused(true);
    }
  };

  const handleContinue = () => {
    setShowPopup(false);
    setIsPaused(false);
  };

  const handleExit = () => {
    navigate("/");
  };

  const initializeThreeJS = () => {
    if (!gameContainerRef.current) {
      return;
    }

    setTimeout(() => {
      let scene, camera, renderer;

      scene = new THREE.Scene();
      const fov = 60;
      const aspect = window.innerWidth / window.innerHeight;
      const near = 0.1;
      const far = 1000;
      camera = new THREE.PerspectiveCamera(fov, aspect, near, far);
      scene.add(camera);

      renderer = new THREE.WebGLRenderer({
        canvas: gameContainerRef.current,
        antialias: true,
      });
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.setPixelRatio(window.devicePixelRatio || 1);
      renderer.setClearColor(0x000000, 0.0);
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;

      const handleResize = () => {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
      };

      window.addEventListener("resize", handleResize, false);

      const rocket = new Rocket();
      rocket.mesh.scale.set(0.005, 0.005, 0.005);
      rocket.mesh.rotation.x = -Math.PI / 2;
      scene.add(rocket.mesh);

      const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
      scene.add(ambientLight);

      const pointLight = new THREE.PointLight(0xffffff, 300);
      pointLight.position.set(0, 5, 10);
      scene.add(pointLight);

      const keyState = {
        ArrowUp: false,
        ArrowDown: false,
        ArrowLeft: false,
        ArrowRight: false,
      };
      const handleKeyDown = (event) => {
        if (keyState.hasOwnProperty(event.code)) keyState[event.code] = true;
      };
      const handleKeyUp = (event) => {
        if (keyState.hasOwnProperty(event.code)) keyState[event.code] = false;
      };

      document.addEventListener("keydown", handleKeyDown);
      document.addEventListener("keyup", handleKeyUp);
      window.addEventListener("keydown", function(e) {
        // space, page up, page down and arrow keys:
        if([32, 33, 34, 37, 38, 39, 40].indexOf(e.keyCode) > -1) {
            e.preventDefault();
        }
      }, false);


      const moveSpeed = 0.2;
      const turnSpeed = 0.02;
      const tiltSpeed = 0.02;
      const cameraOffset = new THREE.Vector3(3, 1, 8);

      const dustParticles = new THREE.BufferGeometry();
      const dustCount = 10000;
      const dustRadius = 200;
      const positions = new Float32Array(dustCount * 3);
      for (let i = 0; i < dustCount; i++) {
        positions[i * 3] = (Math.random() - 0.5) * dustRadius * 2;
        positions[i * 3 + 1] = (Math.random() - 0.5) * dustRadius * 2;
        positions[i * 3 + 2] = (Math.random() - 0.5) * dustRadius * 2;
      }
      dustParticles.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const dustMaterial = new THREE.PointsMaterial({ color: 0xffffff, size: 0.4 });
      const dust = new THREE.Points(dustParticles, dustMaterial);
      scene.add(dust);

      let touchX = null;
      let touchY = null;

      const handleTouchStart = (event) => {
        touchX = event.touches[0].clientX;
        touchY = event.touches[0].clientY;
      };

      const handleTouchEnd = (event) => {
        if (touchX === null || touchY === null) {
          return;
        }

        const screenCenterX = window.innerWidth / 2;
        const screenCenterY = window.innerHeight / 2;

        if (touchX > screenCenterX) {
          keyState.ArrowRight = true;
        } else {
          keyState.ArrowLeft = true;
        }
      
        if (touchY > screenCenterY) {
          keyState.ArrowDown = true;
        } else {
          keyState.ArrowUp = true;
        }

        setTimeout(() => {
          for (const key in keyState) {
            keyState[key] = false;
          }
        }, 100); // Adjust the delay as needed

        touchX = null;
        touchY = null;
      };

      function updateRocketPosition() {
        if (keyState.ArrowUp) {
          rocket.mesh.rotation.x += turnSpeed;
        } else if (keyState.ArrowDown) {
          rocket.mesh.rotation.x -= turnSpeed;
        }

        if (keyState.ArrowLeft) {
          rocket.mesh.rotation.z += tiltSpeed;
        } else if (keyState.ArrowRight) {
          rocket.mesh.rotation.z -= tiltSpeed;
        }

        const direction = new THREE.Vector3(0, 1, 0);
        direction.applyQuaternion(rocket.mesh.quaternion);
        rocket.mesh.position.add(direction.multiplyScalar(moveSpeed));

        const desiredCameraPosition = rocket.mesh.position
          .clone()
          .add(direction.clone().multiplyScalar(-cameraOffset.x))
          .add(new THREE.Vector3(0, cameraOffset.y, cameraOffset.z));

        camera.position.lerp(desiredCameraPosition, 0.1);
        camera.lookAt(rocket.mesh.position);
        ambientLight.position.copy(camera.position);
        pointLight.position.copy(camera.position);

        const dustPositions = dustParticles.attributes.position.array;
        for (let i = 0; i < dustCount; i++) {
          dustPositions[i * 3] += direction.x * moveSpeed * -2;
          dustPositions[i * 3 + 1] += direction.y * moveSpeed * -2;
          dustPositions[i * 3 + 2] += direction.z * moveSpeed * -2;

          const resetThreshold = dustRadius * 1.5;
          if (dustPositions[i * 3] > resetThreshold || dustPositions[i * 3] < -resetThreshold) {
            dustPositions[i * 3] = (Math.random() - 0.5) * dustRadius * 2;
          }
          if (dustPositions[i * 3 + 1] > resetThreshold || dustPositions[i * 3 + 1] < -resetThreshold) {
            dustPositions[i * 3 + 1] = (Math.random() - 0.5) * dustRadius * 2;
          }
          if (dustPositions[i * 3 + 2] > resetThreshold || dustPositions[i * 3 + 2] < -resetThreshold) {
            dustPositions[i * 3 + 2] = (Math.random() - 0.5) * dustRadius * 2;
          }
        }
        dustParticles.attributes.position.needsUpdate = true;
      }

      const celestialBodies = [
        { name: "Saturn", position: new THREE.Vector3(40, 20, -30), url: "/about", path: Saturn, distance: 4 },
        { name: "Sun", position: new THREE.Vector3(20, -40, 35), url: "/projects", path: Sun, distance: 10 },
        { name: "Moon", position: new THREE.Vector3(-35, 30, 40), url: "/education", path: Moon, distance: 2 },
        { name: "BlackHole", position: new THREE.Vector3(-45, -15, -25), url: "/experience", path: BlackHole, distance: 5 },
        { name: "Galaxy", position: new THREE.Vector3(10, 45, 20), url: "/contact", path: Galaxy, distance: 8 },
        { name: "Earth", position: new THREE.Vector3(0, 0, -100), url: "/", path: Earth, distance: 4 },
      ];

      celestialBodies.forEach((body) => {
        const model = new body.path();
        model.mesh.position.copy(body.position);
        scene.add(model.mesh);
        body.mesh = model.mesh;
      });

      function checkCollisions() {
        celestialBodies.forEach((body) => {
          if (rocket.mesh.position.distanceTo(body.mesh.position) < body.distance) {
            navigate(body.url);
          }
        });
      }

      function animate() {
        requestAnimationFrame(animate);
        if (!showPopup && !isPaused) {
          updateRocketPosition();
        }
        checkCollisions();
        renderer.render(scene, camera);
      }

      animate();

      const canvas = gameContainerRef.current;
      canvas.addEventListener("touchstart", handleTouchStart);
      canvas.addEventListener("touchend", handleTouchEnd);

      return () => {
        window.removeEventListener("resize", handleResize);
        document.removeEventListener("keydown", handleKeyDown); // Re-added keyboard listener
        document.removeEventListener("keyup", handleKeyUp);   // Re-added keyboard listener
        canvas.removeEventListener("touchstart", handleTouchStart);
        canvas.removeEventListener("touchend", handleTouchEnd);
        gameContainerRef.current?.removeChild(renderer.domElement);
      };
    }, 0);
  };

  useLayoutEffect(() => {
    if (document.readyState === "complete") {
      initializeThreeJS();
    } else {
      window.addEventListener("load", initializeThreeJS);
      return () => window.removeEventListener("load", initializeThreeJS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, isPaused, showPopup]);

  return (
    <div id="wrap">
      <div id="pause"
        style={{
          position: 'absolute',
          top: '20px',
          left: '20px',
          zIndex: 11,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          color: 'white',
          padding: '8px',
          borderRadius: '5px',
          cursor: 'pointer',
          fontSize: isPaused ? '3em' : '2em',
          lineHeight: '1',
        }}
        onClick={handlePauseToggle}
      >
        {isPaused ? '\u25B6' : '\u23F8'}
      </div>
      {(showPopup || isPaused) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backgroundImage: `url(${starsImage})`,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10,
          }}
        >
          <div
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              padding: '20px',
              borderRadius: '8px',
              width: '80%',
              maxWidth: '600px',
              color: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <h2>Welcome to Ayaan's Space Realm!</h2><br></br>
            <div
              style={{
                maxHeight: '400px',
                overflowY: 'auto',
                marginBottom: '50px',
                padding: '10px',
                borderRadius: '4px',
              }}
            >
              <img
                src={game_rules}
                alt="Use keyboard/touch to crash the rocket into celestial bodies and navigate to different pages"
                style={{ maxWidth: '100%', display: 'block', border: 'none' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleContinue}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#007bff',
                  width: 'auto',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Continue
              </button>
              <button
                onClick={handleExit}
                style={{
                  padding: '10px 20px',
                  width: 'auto',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Exit
              </button>
            </div>
          </div>
        </div>
      )}
      <canvas className="webgl" ref={gameContainerRef}></canvas>
    </div>
  );
};

export default Game;